/**
 * 出图按钮的闸门(从 FreeCreateBody 抽出来的纯函数)。
 *
 * ★这里**没有** mask 相关的条件,是有意的:mask 在提交那一刻才导出+上传(见
 * `useFreeCreateActions.handleGenerate` 的 resolveMask),整段都在提交自己的忙碌态里,
 * 不存在"边传边点"的窗口需要挡。先前的版本是每笔预传 + 在这里挡两道闸,被 codex 连抓
 * 5 轮竞态 —— 窗口是**预传那个结构**造出来的,补丁再多也补不完。
 */

export interface FreeCreateGateInput {
  /** 改图态(挂了原图)= true;从零出图 = false。 */
  isI2I: boolean;
  submitting: boolean;
  /** AI 润色进行中(V11.5:润色期间也不许出图)。 */
  polishing: boolean;
  /** 定时发布已选好时间(立即发布恒 true)。 */
  scheduleReady: boolean;
  /** 原图正在上传。 */
  refUploading: boolean;
  /** 已有原图(改图态必需)。 */
  hasRefImage: boolean;
  prompt: string;
  /** 能力卡打的底里还留着「〈…〉」待填段。 */
  remixSlotUnfilled: boolean;
  /** 套图模式已选至少一个投放点位。 */
  groupPointsOk: boolean;
  /**
   * 0926:出图能力表拉不到(档位/渠道未知 → 报不出价)。此时点生成只会得到一张没价的单或 4xx,
   * 按钮直接禁用,原因贴着按钮说({@link CAPABILITIES_DOWN_REASON})。
   */
  serviceUnavailable?: boolean;
}

/** 能力表挂了时贴在「生成」按钮旁的话(api PosterAiClient 对外报错同一句)。 */
export const CAPABILITIES_DOWN_REASON = "出图服务暂时不可用,请稍后刷新重试";

/** 报不出价时底栏的占位(不兜旧值、不兜 0)。 */
export const PRICE_PENDING_TEXT = "价格待系统核算";

/** 改图态指令下限 2 字(卡片打的底本来就短),从零出图 5 字。 */
export const MIN_PROMPT_I2I = 2;
export const MIN_PROMPT_T2I = 5;

export function canSubmitFreeCreate(s: FreeCreateGateInput): boolean {
  if (s.serviceUnavailable) return false;
  const baseReady = !s.submitting && !s.polishing && s.scheduleReady;
  if (!baseReady) return false;
  if (s.isI2I) {
    return (
      !s.refUploading &&
      s.hasRefImage &&
      s.prompt.trim().length >= MIN_PROMPT_I2I &&
      !s.remixSlotUnfilled
    );
  }
  return s.prompt.trim().length >= MIN_PROMPT_T2I && s.groupPointsOk;
}

/**
 * 修复波 0926 第八批 Z3:按钮变灰时贴在旁边的「为什么点不了」—— 与 {@link canSubmitFreeCreate}
 * **逐条同源**(同一个输入、同一组下限常量),保证:按钮灰且不在忙 ⇔ 这里给出一句原因。
 * 此前只有「能力表挂了」有原因,缺描述 / 缺原图 / 台本全空时按钮只是变灰,用户不知道差什么。
 *
 * 返回 null 的情况:能点;或正在提交 / 润色(按钮自己显示忙碌文案,不需要再解释)。
 */
export function freeCreateDisabledReason(s: FreeCreateGateInput): string | null {
  if (s.serviceUnavailable) return CAPABILITIES_DOWN_REASON;
  if (s.submitting || s.polishing) return null;
  if (!s.scheduleReady) return "请先选好定时发布的时间";
  const len = s.prompt.trim().length;
  if (s.isI2I) {
    if (s.refUploading) return "原图还在上传,传完就能生成";
    if (!s.hasRefImage) return "请先上传要修改的原图";
    if (len === 0) return "请先写一句要怎么改";
    if (len < MIN_PROMPT_I2I) return `修改说明至少 ${MIN_PROMPT_I2I} 个字`;
    if (s.remixSlotUnfilled) return "指令里还有〈…〉待填段,填好再生成";
    return null;
  }
  if (len === 0) return "请先写画面描述";
  if (len < MIN_PROMPT_T2I) return `画面描述至少 ${MIN_PROMPT_T2I} 个字`;
  if (!s.groupPointsOk) return "台本卡还都是空的,至少写好一张再生成";
  return null;
}
